// BillingStripeBody — subscription / credits / last-invoice / payment-method cards plus
// "Manage Billing in Stripe" (admin only). Portal click → billingService.openPortal().
import { useState } from 'react';
import type { BillingBody } from '@/services/api/billingService';
import { billingService } from '@/services/api';
import { Badge, Button } from '@/components/ui';
import { formatDateTimeShort } from '@/lib/datetime';
import type { BadgeTone } from '@/components/ui';

function statusTone(status: string): BadgeTone {
  if (status === 'active') return 'success';
  if (status === 'trialing') return 'info';
  if (status === 'past_due') return 'warning';
  return 'secondary';
}

function capitalize(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

export default function BillingStripeBody({
  data,
  onUpgradePlan,
  onBuyCredits,
}: {
  data: BillingBody;
  onUpgradePlan?: () => void;
  onBuyCredits?: () => void;
}) {
  const { subscription, planMeta, balance, lastInvoice, paymentMethods, isAdmin } = data;
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState('');

  const hasSubscription = Boolean(subscription && subscription.status);
  const activePaymentMethods = paymentMethods.filter((pm) => pm.isActive);

  // Credits can only be spent while the plan is active/trialing/in its past-due grace. Once the
  // subscription has ended (canceled/expired/unpaid), any remaining credits are frozen — they stay
  // on the balance but can't be used until the org resubscribes. Surface that here so a bare
  // "Plan/Top-up credits: N" beside a canceled badge isn't mistaken for usable balance. Plan and
  // top-up get separate lines: top-ups persist indefinitely, plan credits are re-granted on
  // resubscribe.
  const creditsUsable = subscription
    ? ['active', 'trialing', 'past_due'].includes(subscription.status)
    : false;
  const planFrozen = Boolean(balance && balance.planBalance > 0 && !creditsUsable);
  const topupFrozen = Boolean(balance && balance.topupBalance > 0 && !creditsUsable);
  const canBuyCredits = isAdmin && creditsUsable;

  const openPortal = async () => {
    setError('');
    setPortalLoading(true);
    try {
      const { url } = await billingService.openPortal();
      if (!url) throw new Error('Could not open portal.');
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open portal.');
      setPortalLoading(false);
    }
  };

  return (
    <>
      <div className="org-plan-grid">
        <article className="org-plan-card">
          <span className="org-plan-card-icon org-plan-card-icon--navy" aria-hidden>
            <i className="bi bi-receipt" />
          </span>
          <h3 className="org-plan-card-title">Subscription</h3>
          {hasSubscription && subscription ? (
            <>
              <p className="org-plan-card-plan">
                <span className="org-plan-name">
                  {planMeta?.name || subscription.planCode || 'Unknown'}
                </span>{' '}
                <Badge tone={statusTone(subscription.status)}>{subscription.status}</Badge>
              </p>
              <p className="org-plan-card-meta">
                Billing cycle: {subscription.billingCycle || '—'}
              </p>
              <p className="org-plan-card-meta">Seats: {subscription.seats || 1}</p>
              {subscription.trialEndsAt && (
                <p className="org-plan-card-meta">
                  Trial ends: {formatDateTimeShort(subscription.trialEndsAt)}
                </p>
              )}
              {subscription.currentPeriodEnd && (
                <p className="org-plan-card-meta">
                  Next billing: {formatDateTimeShort(subscription.currentPeriodEnd)}
                </p>
              )}
              {subscription.cancelAtPeriodEnd && (
                <div className="alert alert-warning mt-3 mb-0 p-2 small">
                  Subscription will cancel at the end of the current period.
                </div>
              )}
            </>
          ) : (
            <>
              <p className="org-plan-card-plan">
                <span className="org-plan-name">Free</span>{' '}
                <Badge tone="secondary">free plan</Badge>
              </p>
              <p className="org-plan-card-meta">
                0 credits · 1 seat — upgrade to run research jobs, invite teammates, and buy
                top-ups.
              </p>
              {isAdmin ? (
                <Button pill className="mt-3" onClick={onUpgradePlan}>
                  Upgrade plan
                </Button>
              ) : (
                <p className="org-plan-card-meta">
                  Ask your organization owner or admin to upgrade the plan.
                </p>
              )}
            </>
          )}
          {hasSubscription && isAdmin && (
            <button
              type="button"
              className="btn btn-standard org-plan-card-cta"
              onClick={openPortal}
              disabled={portalLoading}
            >
              {portalLoading ? 'Opening...' : 'Manage Billing in Stripe'}
            </button>
          )}
        </article>

        <article className="org-plan-card">
          <span className="org-plan-card-icon org-plan-card-icon--green" aria-hidden>
            <i className="bi bi-coin" />
          </span>
          <h3 className="org-plan-card-title">Credits</h3>
          {balance ? (
            <>
              <p className="org-plan-card-meta">
                <strong>Plan credits:</strong> {balance.planBalance}
              </p>
              <p className="org-plan-card-meta">
                <strong>Top-up credits:</strong> {balance.topupBalance}
              </p>
              <p className="org-plan-card-meta">Reserved (active jobs): {balance.reserved}</p>
              <p className="org-plan-card-meta">1 credit = 10 companies processed.</p>
              {planFrozen && (
                <p className="org-plan-card-meta text-warning mt-2">
                  Your plan is inactive — these {balance.planBalance} plan credits cannot be used
                  until you resubscribe to a plan.
                </p>
              )}
              {topupFrozen && (
                <p className="org-plan-card-meta text-warning mt-2">
                  Your {balance.topupBalance} top-up credits do not expire, but cannot be used until
                  you resubscribe to a plan.
                </p>
              )}
            </>
          ) : (
            <p className="org-plan-card-meta">No credits yet — pick a plan to get started.</p>
          )}
          {canBuyCredits && (
            <Button pill className="org-plan-card-cta" onClick={onBuyCredits}>
              Buy more credits
            </Button>
          )}
        </article>

        {lastInvoice && lastInvoice.stripeInvoiceId && (
          <article className="org-plan-card">
            <span className="org-plan-card-icon org-plan-card-icon--navy" aria-hidden>
              <i className="bi bi-receipt-cutoff" />
            </span>
            <h3 className="org-plan-card-title">Last Invoice</h3>
            <p className="org-plan-card-plan">
              ${((lastInvoice.amount || 0) / 100).toFixed(2)}{' '}
              {(lastInvoice.currency || 'usd').toUpperCase()}{' '}
              <Badge tone="secondary">{lastInvoice.status}</Badge>
            </p>
            {lastInvoice.paidAt && (
              <p className="org-plan-card-meta">Paid: {formatDateTimeShort(lastInvoice.paidAt)}</p>
            )}
            {(lastInvoice.hostedUrl || lastInvoice.pdfUrl) && (
              <div className="org-plan-card-actions">
                {lastInvoice.hostedUrl && (
                  <a
                    href={lastInvoice.hostedUrl}
                    target="_blank"
                    rel="noopener"
                    className="btn btn-standard"
                  >
                    View invoice
                  </a>
                )}
                {lastInvoice.pdfUrl && (
                  <a
                    href={lastInvoice.pdfUrl}
                    target="_blank"
                    rel="noopener"
                    className="btn btn-standard"
                  >
                    Download PDF
                  </a>
                )}
              </div>
            )}
          </article>
        )}

        {activePaymentMethods.length > 0 && (
          <article className="org-plan-card">
            <span className="org-plan-card-icon org-plan-card-icon--navy" aria-hidden>
              <i className="bi bi-credit-card" />
            </span>
            <h3 className="org-plan-card-title">Payment Methods</h3>
            {activePaymentMethods.map((pm, i) => (
              <p className="org-plan-card-meta" key={`${pm.brand}-${pm.last4}-${i}`}>
                {capitalize(pm.brand)} •••• {pm.last4}{' '}
                <span>
                  exp {pm.expMonth}/{pm.expYear}
                </span>{' '}
                {pm.isDefault && <Badge tone="info">default</Badge>}
              </p>
            ))}
          </article>
        )}
      </div>

      {error && (
        <div className="alert alert-danger mt-4" role="alert">
          {error}
        </div>
      )}
    </>
  );
}
